/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState, Fragment } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";

// Soft UI Dashboard PRO React example components
import DataTable from "Tables/DataTable";
import DataTableHeadCell from "Tables/DataTableMultiline/DataTableHeadCell";
import DataTableBodyCell from "Tables/DataTableMultiline/DataTableBodyCell";
import CheckBox from "@mui/material/Checkbox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

function MultiRow({ rowdata, multiRowKey, checkboxSelection, noEndBorder, handleExpandCollapse }) {
  const [open, setOpen] = useState(null);

  const row = rowdata;
  const key = multiRowKey;
  const rowStableId = row.id || row.original.id || key;
  const key_string = rowStableId.toString();

  function handleExpandeCollapse(e, row) {
    e.preventDefault();
    row.original.openSwitch = !row.original.openSwitch;
    handleExpandCollapse({
      key: "expand",
      value: row.original.bet_id,
      extra: row.original.openSwitch,
    });
  }

  function checkOpenSwitch() {
    return rowdata.original.openSwitch;
  }

  return (
    <Fragment key={key_string + "-combo"}>
      <TableRow
        key={key_string}
        {...row.getRowProps()}
        className={key % 2 > 0 ? "multi-row-table odd-row" : "multi-row-table even-row"}
      >
        {row.cells.map((cell, key) =>
          cell.column.id == "multiline" ? null : cell.column.id == "openSwitch" ? (
            <DataTableBodyCell key={key + "-" + key_string + "-switch"}>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={(e) => handleExpandeCollapse(e, row)}
              >
                {row.original.openSwitch ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </DataTableBodyCell>
          ) : (
            <DataTableBodyCell
              key={key}
              noBorder={noEndBorder && row.cells.length - 1 === key}
              align={cell.column.align ? cell.column.align : "left"}
              {...cell.getCellProps()}
            >
              {cell.render("Cell")}
            </DataTableBodyCell>
          )
        )}
      </TableRow>
      <tr key={key_string + "-extraline"} className="collapsable-row-table">
        <td colSpan={10}>
          <Collapse in={checkOpenSwitch()} timeout="auto" unmountOnExit>
            <SoftBox sx={{ margin: 1 }}>
              <DataTable
                table={row.original.multiline}
                // bottomPadding={26}
                tableClassName="advanced-riskmanagement-table-betodds"
                hiddenColumns={["tm"]}
                isSorted={true}
                showTotalEntries={false}
                autoResetPageDefault={false}
                autoResetGlobalFilter={false}
                autoResetSortBy={false}
                canSearch={false}
                refreshEnabled={false}
                showAllResults={true}
              ></DataTable>
            </SoftBox>
          </Collapse>
        </td>
      </tr>
    </Fragment>
  );
}

function DataTableMultiline({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  hiddenColumns,
  bodyClassName,
  headerClassName,
  pagination,
  isSorted,
  noEndBorder,
  autoResetPageDefault,
  autoResetGlobalFilterDefault,
  autoResetSortByDefault,
  bottomPadding,
  className,
  tableClassName,
  searchValue,
  openBackDrop,
  informParent,
  refreshEnabled,
  tableName,
  checkboxSelection,
  handleExpandCollapse,
  showAllResults,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries ? entriesPerPage.entries : [5, 10, 15, 20, 25];
  const columns = useMemo(
    () =>
      hiddenColumns
        ? table.columns.filter((item) => hiddenColumns.indexOf(item.accessor) < 0)
        : table.columns,
    [table.columns, hiddenColumns]
  );
  const data = useMemo(() => table.rows, [table.rows]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        hiddenColumns: hiddenColumns ? hiddenColumns : [],
      },
      autoResetPage: autoResetPageDefault ? autoResetPageDefault : false,
      autoResetGlobalFilter: autoResetGlobalFilterDefault ? autoResetGlobalFilterDefault : false,
      autoResetSortBy: autoResetSortByDefault ? autoResetSortByDefault : false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  const selectedCount = useMemo(
    () => rows.reduce((count, row) => (row.original.selected ? count + 1 : count), 0),
    [rows]
  );
  const allRowsSelected = rows.length > 0 && selectedCount === rows.length;
  const partiallySelected = selectedCount > 0 && selectedCount < rows.length;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = ({ value }) => setPageSize(value);

  // Render the paginations
  const renderPagination = () => {
    let paginationSteps = JSON.parse(JSON.stringify(pageOptions));
    let lastIndex = pageIndex;
    if (lastIndex > paginationSteps.length) {
      lastIndex = paginationSteps.length;
      gotoPage(0);
    }

    paginationSteps = paginationSteps.filter((item, index) => {
      if (Math.abs(index - lastIndex) <= 2) return true;
    });
    return paginationSteps.map((option) => (
      <SoftPagination
        item
        key={option}
        onClick={() => gotoPage(Number(option))}
        active={pageIndex === option}
      >
        {option + 1}
      </SoftPagination>
    ));
  };

  // Handler for the input to set the pagination index
  
  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  
  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  useEffect(() => {
    if (pageIndex === 0) return;
    if (rows.length <= pageSize || rows.length < entriesStart) {
      gotoPage(0);
    }
  }, [rows.length, pageSize, pageIndex, entriesStart, gotoPage]);

  function searchFunction(e) {
    e.preventDefault();
    e.currentTarget.value = e.currentTarget.value;
    setSearch(e.currentTarget.value || undefined);
    if (informParent) informParent({ key: "search", value: e.currentTarget.value || undefined });
  }

  function clearSearch(e) {
    e.preventDefault();
    if (informParent) informParent({ key: "search", value: null });
    setSearch(undefined);
  }

  function refreshData(e) {
    e.preventDefault();
    if (informParent) informParent({ key: "refresh", value: null });
  }

  function handleSelectAllCheckbox(e) {
    e.preventDefault();
    const shouldSelectAll = !allRowsSelected;

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      row.original.selected = shouldSelectAll;
    }

    if (informParent) {
      informParent({
        key: "selectAll",
        value: rows.map((r) => {
          return r.original;
        }),
        extra: shouldSelectAll ? "all" : "none",
      });
    }
  }

  function handleRowSelection(e, row) {
    e.preventDefault();
    row.original.selected = !row.original.selected;
    informParent({ key: "selectOne", value: row.original.id, extra: row.original.selected });
  }

  useEffect(() => {
    //console.log("searchValue: ", searchValue);
    if (searchValue) {
      setGlobalFilter(searchValue.toString());
      setSearch(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    //console.log("table instance ", tableInstance);
    if (!document.getElementById(tableName + "-custom-search")) return;
    if (search) {
      document.getElementById(tableName + "-custom-search").value = search;
      onSearchChange(search);
    } else {
      document.getElementById(tableName + "-custom-search").value = "";
      onSearchChange(search);
    }
  }, [search]);


  return (
    <TableContainer
      sx={{ boxShadow: "none" }}
      className={className ? className : ""}
      id="table_container"
    >
      <div id="loader" style={{ zIndex: openBackDrop ? 100 : -1 }}>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={openBackDrop !== undefined ? openBackDrop : false}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
      <div
        id="zero-data"
        style={{ zIndex: 100, display: data.length == 0 && !openBackDrop ? "" : "none" }}
      >
        {/* {data.length == 0 && !openBackDrop ? "NO DATA" : ""} */}
      </div>
      {entriesPerPage || canSearch ? (
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={3}
          className="table-fixed-tools"
        >
          {entriesPerPage && !showAllResults && (
            <SoftBox display="flex" alignItems="center" className="table-fixed-tools-left">
              <SoftSelect
                defaultValue={{ value: defaultValue, label: defaultValue }}
                options={entries.map((entry) => ({ value: entry, label: entry }))}
                onChange={setEntriesPerPage}
                size="small"
              />
              <SoftTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </SoftTypography>
            </SoftBox>
          )}
          {canSearch && (
            <SoftBox
              width={search ? "16rem" : "12rem"}
              ml="auto"
              className="table-fixed-tools-right"
            >
              <SoftBox>
                <Button
                  color="text"
                  fontWeight="regular"
                  onClick={(e) => clearSearch(e)}
                  sx={{ display: search ? "" : "none" }}
                >
                  <Icon>clear_icon</Icon>
                </Button>
              </SoftBox>
              <SoftInput
                id={tableName + "-custom-search"}
                placeholder="Search..."
                onChange={(e) => searchFunction(e)}
              />
              <SoftBox
                style={{
                  position: "absolute",
                  right: "-3.5rem",
                  display: refreshEnabled && informParent ? "" : "none",
                }}
              >
                <Button onClick={(e) => refreshData(e)}>
                  <Icon>refresh_icon</Icon>
                </Button>
              </SoftBox>
            </SoftBox>
          )}
        </SoftBox>
      ) : null}
      <Table {...getTableProps()} className={tableClassName ? tableClassName : ""}>
        <SoftBox component="thead" className={headerClassName ? headerClassName : ""}>
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {checkboxSelection ? (
                <DataTableHeadCell
                  key={"rowSelection"}
                  width={"50px"}
                  maxWidth="50px"
                  align={"left"}
                  sorted={false}
                >
                  <CheckBox
                    className={
                      partiallySelected
                        ? "select-all-checkbox-indeterminate"
                        : allRowsSelected
                        ? "select-all-checkbox"
                        : ""
                    }
                    onClick={handleSelectAllCheckbox}
                    checked={allRowsSelected}
                    indeterminate={partiallySelected}
                  ></CheckBox>
                </DataTableHeadCell>
              ) : null}
              {headerGroup.headers.map((column, key) => (
                <DataTableHeadCell
                  key={key}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </SoftBox>
        <TableBody
          {...getTableBodyProps()}
          className={bodyClassName ? bodyClassName : ""}
          id="table_body"
          sx={{ display: table.rows.length > 0 ? "" : "block" }}
        >
          {showAllResults
            ? rows.map((row, key) => {
                prepareRow(row);
                return row.original.multiline ? (
                  <MultiRow
                    rowdata={row}
                    multiRowKey={key}
                    checkboxSelection={checkboxSelection}
                    noEndBorder={noEndBorder}
                    handleExpandCollapse={handleExpandCollapse}
                  ></MultiRow>
                ) : (
                  // <Row key={row.key} row={row} />
                  <TableRow
                    key={row.id || row.original.id || key}
                    {...row.getRowProps()}
                    className={
                      row.original.linked || row.original.modified
                        ? "linked-event"
                        : row.original.time_exceeded
                        ? "time-exceeded"
                        : "not-linked-event"
                    }
                  >
                    {checkboxSelection ? (
                      <DataTableBodyCell
                        key={key + "-selection"}
                        width={"50px"}
                        maxWidth="50px"
                        align={"left"}
                      >
                        <CheckBox
                          checked={row.original.selected}
                          onClick={(e) => handleRowSelection(e, row)}
                        ></CheckBox>
                      </DataTableBodyCell>
                    ) : null}
                    {row.cells.map((cell, key) =>
                      key == "multiline" ? null : (
                        <DataTableBodyCell
                          key={key}
                          noBorder={noEndBorder && rows.length - 1 === key}
                          align={cell.column.align ? cell.column.align : "left"}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell")}
                        </DataTableBodyCell>
                      )
                    )}
                  </TableRow>
                );
              })
            : page.map((row, key) => {
                prepareRow(row);
                return row.original.multiline ? (
                  <MultiRow
                    key={row.id || row.original.id || key}
                    {...row.getRowProps()}
                    rowdata={row}
                    multiRowKey={key}
                    checkboxSelection={checkboxSelection}
                    noEndBorder={noEndBorder}
                    handleExpandCollapse={handleExpandCollapse}
                  ></MultiRow>
                ) : (
                  // <Row key={row.key} row={row} />
                  <TableRow
                    key={row.id || row.original.id || key}
                    {...row.getRowProps()}
                    className={
                      row.original.linked || row.original.modified
                        ? "linked-event"
                        : row.original.time_exceeded
                        ? "time-exceeded"
                        : "not-linked-event"
                    }
                  >
                    {checkboxSelection ? (
                      <DataTableBodyCell
                        key={key + "-selection"}
                        width={"50px"}
                        maxWidth="50px"
                        align={"left"}
                      >
                        <CheckBox
                          checked={row.original.selected}
                          onClick={(e) => handleRowSelection(e, row)}
                        ></CheckBox>
                      </DataTableBodyCell>
                    ) : null}
                    {row.cells.map((cell, key) =>
                      key == "multiline" ? null : (
                        <DataTableBodyCell
                          key={key}
                          noBorder={noEndBorder && rows.length - 1 === key}
                          align={cell.column.align ? cell.column.align : "left"}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell")}
                        </DataTableBodyCell>
                      )
                    )}
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>

      <SoftBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        pb={bottomPadding ? bottomPadding : !showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        className="table-fixed-footer"
      >
        {showTotalEntries && (
          <SoftBox mb={{ xs: 3, sm: 0 }} className="table-fixed-footer-left">
            <SoftTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </SoftTypography>
          </SoftBox>
        )}
        {pageOptions.length > 1 && (
          <SoftPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {canPreviousPage && (
              <SoftPagination item onClick={() => gotoPage(0)}>
                <Icon sx={{ fontWeight: "bold" }}>first_page_outlined</Icon>
              </SoftPagination>
            )}
            {canPreviousPage && (
              <SoftPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </SoftPagination>
            )}
            {renderPagination()}
            {pageOptions.length > 6 && pageIndex + 1 < pageOptions.length - 2 ? (
              <>
                <SoftPagination>...</SoftPagination>
                <SoftPagination item onClick={() => gotoPage(pageOptions.length - 1)}>
                  {pageOptions.length}
                </SoftPagination>
              </>
            ) : (
              ""
            )}
            {canNextPage && (
              <SoftPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </SoftPagination>
            )}
            {canNextPage && (
              <SoftPagination item onClick={() => gotoPage(pageOptions.length - 1)}>
                <Icon sx={{ fontWeight: "bold" }}>last_page_outlined</Icon>
              </SoftPagination>
            )}
          </SoftPagination>
        )}
      </SoftBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTableMultiline.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25, 30] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  refreshEnabled: false,
  tableName: "soft-table-multi",
  showAllResults: false,
};

// Typechecking props for the DataTable
DataTableMultiline.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  hiddenColumns: PropTypes.array,
  bodyClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  autoResetPageDefault: PropTypes.bool,
  autoResetGlobalFilterDefault: PropTypes.bool,
  autoResetSortByDefault: PropTypes.bool,
  bottomPadding: PropTypes.number,
  className: PropTypes.string,
  tableClassName: PropTypes.string,
  searchValue: PropTypes.string,
  openBackDrop: PropTypes.bool,
  informParent: PropTypes.func,
  refreshEnabled: PropTypes.bool,
  tableName: PropTypes.string,
  checkboxSelection: PropTypes.bool,
  handleExpandCollapse: PropTypes.func,
  showAllResults: PropTypes.bool,
};

MultiRow.propTypes = {
  rowdata: PropTypes.object,
  multiRowKey: PropTypes.number,
  checkboxSelection: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  handleExpandCollapse: PropTypes.func,
};

// ExtraRow.propTypes = {
//   row: PropTypes.shape({
//     original: PropTypes.object,
//     cells: PropTypes.array,
//   }).isRequired,
//   key: PropTypes.any,
// };

export default DataTableMultiline;
